<template>
	<div
		class="auth-dialog"
		role="dialog"
		aria-modal="true"
		:aria-label="
			forgotPassContentVisible ? $t('Забыли пароль?') : $t('Войти')
		"
	>
		<div class="auth-dialog__overlay" @click="closeModal"></div>

		<div class="auth-dialog__glow"></div>

		<div class="auth-dialog__viewport">
			<section class="auth-dialog__card">
				<button
					type="button"
					class="auth-dialog__close"
					:aria-label="$t('Close')"
					@click="closeModal"
				>
					<svg
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M6 6L18 18"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="square"
						/>

						<path
							d="M18 6L6 18"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="square"
						/>
					</svg>
				</button>

				<button
					v-if="forgotPassContentVisible"
					type="button"
					class="auth-dialog__back"
					@click="switchToLogin"
				>
					<svg
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M15 18L9 12L15 6"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="square"
							stroke-linejoin="round"
						/>
					</svg>

					<span>{{ $t('Назад') }}</span>
				</button>

				<header class="auth-dialog__header">
					<h2 class="auth-dialog__title">
						{{
							forgotPassContentVisible
								? $t('Забыли пароль?')
								: $t('С возвращением')
						}}
					</h2>

					<p class="auth-dialog__description">
						{{ authDescription }}
					</p>
				</header>

				<transition name="auth-content" mode="out-in">
					<div
						v-if="loginContentVisible"
						key="login"
						class="auth-dialog__content"
					>
						<div class="auth-tabs">
							<button
								type="button"
								class="auth-tabs__button auth-tabs__button_active"
							>
								{{ $t('Вход') }}
							</button>

							<button
								type="button"
								class="auth-tabs__button"
								@click="openSignUp"
							>
								{{ $t('Регистрация') }}
							</button>
						</div>

						<form class="auth-form" @submit.prevent="submitLogin">
							<label class="auth-field">
								<span class="auth-field__label">
									{{ $t('Email') }}
								</span>

								<input
									v-model.trim="email"
									type="email"
									class="auth-field__input"
									:placeholder="$t('you@mail.com')"
									autocomplete="email"
									maxlength="120"
								/>
							</label>

							<label class="auth-field">
								<span class="auth-field__label">
									{{ $t('Пароль') }}
								</span>

								<div class="auth-field__control">
									<input
										v-model="pass"
										:type="passwordVisible ? 'text' : 'password'"
										class="auth-field__input"
										:placeholder="$t('Password')"
										autocomplete="current-password"
										@keyup.enter="submitLogin"
									/>

									<button
										type="button"
										class="auth-field__toggle"
										:aria-label="
											$t(
												passwordVisible
													? 'Скрыть пароль'
													: 'Показать пароль',
											)
										"
										@click="passwordVisible = !passwordVisible"
									>
										<svg
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											aria-hidden="true"
										>
											<path
												d="M2 12C4.4 7.8 7.7 5.7 12 5.7C16.3 5.7 19.6 7.8 22 12C19.6 16.2 16.3 18.3 12 18.3C7.7 18.3 4.4 16.2 2 12Z"
												stroke="currentColor"
												stroke-width="1.7"
												stroke-linejoin="round"
											/>

											<circle
												cx="12"
												cy="12"
												r="3"
												stroke="currentColor"
												stroke-width="1.7"
											/>
										</svg>
									</button>
								</div>
							</label>

							<div class="auth-form__forgot">
								<button
									type="button"
									class="auth-form__link"
									@click="switchToForgotPass"
								>
									{{ $t('Забыли пароль?') }}
								</button>
							</div>

							<button
								type="submit"
								class="button shadowed auth-form__submit"
								:disabled="!loginFormIsValid || loginLoading"
							>
								<span>{{ $t('ВОЙТИ') }}</span>
							</button>
						</form>

						<transition name="auth-message">
							<div
								v-if="$parent.error"
								class="auth-message auth-message_error"
							>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									aria-hidden="true"
								>
									<path
										d="M12 8V13"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
									/>

									<path
										d="M12 17H12.01"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
									/>

									<path
										d="M10.3 3.6L2.4 17.2C1.6 18.6 2.6 20.3 4.2 20.3H19.8C21.4 20.3 22.4 18.6 21.6 17.2L13.7 3.6C12.9 2.2 11.1 2.2 10.3 3.6Z"
										stroke="currentColor"
										stroke-width="2"
										stroke-linejoin="round"
									/>
								</svg>

								<span>{{ $parent.error }}</span>
							</div>
						</transition>

						<p class="auth-dialog__footer">
							{{
								$t(
									'Защищено reCAPTCHA. Продолжая, вы принимаете правила сервиса.',
								)
							}}
						</p>
					</div>

					<div v-else key="forgot-password" class="auth-dialog__content">
						<form class="auth-form" @submit.prevent="submitForgotPass">
							<label class="auth-field">
								<span class="auth-field__label">
									{{ $t('Email') }}
								</span>

								<input
									v-model.trim="email"
									type="email"
									class="auth-field__input"
									:placeholder="$t('you@mail.com')"
									autocomplete="email"
								/>
							</label>

							<button
								type="submit"
								class="button shadowed auth-form__submit"
								:disabled="!forgotFormIsValid || forgotPassLoading"
							>
								<span>
									{{ $t('ВОССТАНОВИТЬ ПАРОЛЬ') }}
								</span>
							</button>
						</form>

						<transition name="auth-message">
							<div
								v-if="$parent.successMessage"
								class="auth-message auth-message_success"
							>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									aria-hidden="true"
								>
									<path
										d="M20 6L9 17L4 12"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="square"
										stroke-linejoin="round"
									/>
								</svg>

								<span>
									{{ $parent.successMessage }}
								</span>
							</div>
						</transition>

						<transition name="auth-message">
							<div
								v-if="$parent.error"
								class="auth-message auth-message_error"
							>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									aria-hidden="true"
								>
									<path
										d="M12 8V13"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
									/>

									<path
										d="M12 17H12.01"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
									/>

									<path
										d="M10.3 3.6L2.4 17.2C1.6 18.6 2.6 20.3 4.2 20.3H19.8C21.4 20.3 22.4 18.6 21.6 17.2L13.7 3.6C12.9 2.2 11.1 2.2 10.3 3.6Z"
										stroke="currentColor"
										stroke-width="2"
										stroke-linejoin="round"
									/>
								</svg>

								<span>{{ $parent.error }}</span>
							</div>
						</transition>

						<p class="auth-dialog__footer">
							{{
								$t(
									'Проверьте папку «Спам», если письмо не появилось во входящих.',
								)
							}}
						</p>
					</div>
				</transition>
			</section>
		</div>
	</div>
</template>

<script>
export default {
	name: 'Login',

	props: {
		loginLoading: {
			type: Boolean,
			default: false,
		},

		forgotPassLoading: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['login', 'forgotPass'],

	data() {
		return {
			loginContentVisible: true,
			forgotPassContentVisible: false,

			email: '',
			pass: '',
			passwordVisible: false,
		};
	},

	computed: {
		authDescription() {
			return this.forgotPassContentVisible
				? this.$t('Введите адрес электронной почты.')
				: this.$t('Войдите, чтобы продолжить в BloxNest');
		},

		loginFormIsValid() {
			return this.isValidEmail(this.email) && this.pass.length > 0;
		},

		forgotFormIsValid() {
			return this.isValidEmail(this.email);
		},

		isLoading() {
			return this.loginLoading || this.forgotPassLoading;
		},
	},

	mounted() {
		window.addEventListener('keydown', this.handleKeydown);
	},

	beforeDestroy() {
		window.removeEventListener('keydown', this.handleKeydown);
	},

	methods: {
		handleKeydown(event) {
			if (event.key === 'Escape' && !this.isLoading) {
				this.closeModal();
			}
		},

		closeModal() {
			if (this.isLoading) {
				return;
			}

			if (this.$parent && typeof this.$parent.closeSignIn === 'function') {
				this.$parent.closeSignIn();
			}
		},

		clearParentMessages() {
			if (this.$parent && typeof this.$parent.clearError === 'function') {
				this.$parent.clearError();
			}
		},

		switchToForgotPass() {
			if (this.isLoading) {
				return;
			}

			this.clearParentMessages();

			this.loginContentVisible = false;
			this.forgotPassContentVisible = true;
			this.passwordVisible = false;
		},

		switchToLogin() {
			if (this.isLoading) {
				return;
			}

			this.clearParentMessages();

			this.forgotPassContentVisible = false;
			this.loginContentVisible = true;
		},

		openSignUp() {
			if (this.isLoading) {
				return;
			}

			this.clearParentMessages();

			if (this.$parent && typeof this.$parent.openSignUp === 'function') {
				this.$parent.openSignUp();
			}
		},

		isValidEmail(value) {
			return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
		},

		submitLogin() {
			if (!this.loginFormIsValid || this.loginLoading) {
				return;
			}

			this.clearParentMessages();

			this.$emit('login', {
				username: this.email.trim(),
				password: this.pass,
			});
		},

		submitForgotPass() {
			if (!this.forgotFormIsValid || this.forgotPassLoading) {
				return;
			}

			this.clearParentMessages();

			this.$emit('forgotPass', {
				email: this.email.trim(),
			});
		},
	},
};
</script>
<style scoped lang="scss">
.auth-dialog {
	position: fixed;
	inset: 0;
	z-index: 20;

	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;

	overflow-y: auto;
}

.auth-dialog__overlay {
	position: fixed;
	inset: 0;

	background: radial-gradient(
			circle at 50% -10%,
			rgba(33, 193, 126, 0.09),
			transparent 43%
		),
		rgba(4, 7, 6, 0.9);

	backdrop-filter: blur(8px);
	cursor: pointer;
}

.auth-dialog__glow {
	position: fixed;
	top: -360px;
	left: 50%;

	width: 900px;
	height: 610px;

	transform: translateX(-50%);

	background: radial-gradient(
		circle,
		rgba(35, 218, 139, 0.31) 0%,
		rgba(33, 193, 126, 0.1) 38%,
		transparent 72%
	);

	filter: blur(20px);
	pointer-events: none;
}

.auth-dialog__viewport {
	position: relative;
	z-index: 1;

	width: 100%;
	max-width: 450px;
	margin: auto;
}

.auth-dialog__card {
	position: relative;
	width: 100%;
	padding: 38px;

	background: linear-gradient(
			160deg,
			rgba(33, 193, 126, 0.045),
			transparent 48%
		),
		var(--bunker-86);

	border: 1px solid var(--outer-space);
	border-radius: 13px;

	box-shadow: 0 42px 105px rgba(0, 0, 0, 0.65),
		0 0 70px rgba(33, 193, 126, 0.055),
		inset 2px 2px 0 rgba(255, 255, 255, 0.02),
		inset -2px -2px 0 rgba(0, 0, 0, 0.22);
}

.auth-dialog__close {
	position: absolute;
	top: 15px;
	right: 15px;
	z-index: 3;

	width: 34px;
	height: 34px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;

	color: var(--pewter);
	background: var(--heavy-metal);
	border: 1px solid var(--outer-space);
	border-radius: 8px;

	transition: color 0.2s ease, background-color 0.2s ease,
		border-color 0.2s ease, transform 0.2s ease;

	svg {
		width: 15px;
		height: 15px;
	}

	@media (any-hover: hover) {
		&:hover {
			color: var(--gray-nurse);
			background: var(--outer-space);
			border-color: rgba(33, 193, 126, 0.35);
			transform: rotate(90deg);
		}
	}
}

.auth-dialog__back {
	position: absolute;
	top: 18px;
	left: 18px;
	z-index: 3;

	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 5px;

	font-family: var(--font-1);
	font-size: 11px;
	line-height: 16px;

	color: var(--pewter);
	background: transparent;
	border: 0;

	transition: color 0.2s ease, transform 0.2s ease;

	svg {
		width: 16px;
		height: 16px;
	}

	@media (any-hover: hover) {
		&:hover {
			color: var(--mountain-meadow);
			transform: translateX(-3px);
		}
	}
}

.auth-dialog__header {
	padding: 0 30px;
	margin-bottom: 27px;
	text-align: center;
}

.auth-dialog__title {
	margin: 0;

	font-family: var(--font-2);
	font-size: 27px;
	font-weight: 700;
	line-height: 35px;

	color: var(--gray-nurse);
}

.auth-dialog__description {
	margin: 7px auto 0;

	font-family: var(--font-1);
	font-size: 12px;
	font-weight: 400;
	line-height: 19px;

	color: var(--mantle);
}

.auth-tabs {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 4px;
	padding: 4px;
	margin-bottom: 25px;

	background: rgba(7, 13, 10, 0.72);
	border: 1px solid var(--outer-space);
	border-radius: 9px;
}

.auth-tabs__button {
	position: relative;
	min-height: 39px;
	padding: 9px 12px;

	font-family: var(--font-1);
	font-size: 12px;
	font-weight: 600;
	line-height: 18px;

	color: var(--mantle);
	background: transparent;
	border: 0;
	border-radius: 7px;

	transition: color 0.2s ease, background-color 0.2s ease;

	@media (any-hover: hover) {
		&:hover {
			color: var(--gray-nurse);
		}
	}

	&_active {
		color: var(--racing-green);
		background: linear-gradient(142deg, var(--44), var(--31));

		box-shadow: inset 0 -3px 0 rgba(0, 0, 0, 0.22),
			inset 0 2px 0 rgba(255, 255, 255, 0.15);
	}
}

.auth-form {
	display: flex;
	flex-direction: column;
	gap: 17px;
}

.auth-field {
	display: block;
	margin: 0;
	padding: 0;
}

.auth-field__label {
	display: block;
	margin-bottom: 7px;

	font-family: var(--font-1);
	font-size: 12px;
	font-weight: 500;
	line-height: 17px;

	color: var(--pewter);
}

.auth-field__control {
	position: relative;
}

.auth-field__input {
	display: block;
	width: 100%;
	min-height: 46px;
	margin: 0;
	padding: 11px 14px;

	font-family: var(--font-1);
	font-size: 13px;
	font-weight: 400;
	line-height: 21px;

	color: var(--gray-nurse);
	background: rgba(7, 13, 10, 0.74);
	border: 1px solid var(--outer-space);
	border-radius: 9px;
	outline: none;
	appearance: none;

	box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.34),
		inset -1px -1px 0 rgba(255, 255, 255, 0.025);

	transition: border-color 0.2s ease, background-color 0.2s ease,
		box-shadow 0.2s ease;

	&::placeholder {
		font-family: var(--font-1);
		font-size: 13px;
		color: var(--mantle);
		opacity: 0.62;
	}

	&:focus {
		background: var(--heavy-metal);
		border-color: var(--31);

		box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.3),
			0 0 0 3px rgba(33, 193, 126, 0.1);
	}

	&:-webkit-autofill,
	&:-webkit-autofill:hover,
	&:-webkit-autofill:focus {
		-webkit-text-fill-color: var(--gray-nurse);
		transition: background-color 9999s ease-in-out 0s;
	}
}

.auth-field__control .auth-field__input {
	padding-right: 44px;
}

.auth-field__toggle {
	position: absolute;
	top: 50%;
	right: 11px;

	width: 27px;
	height: 27px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;

	color: var(--mantle);
	background: transparent;
	border: 0;
	transform: translateY(-50%);

	transition: color 0.2s ease;

	svg {
		width: 17px;
		height: 17px;
	}

	@media (any-hover: hover) {
		&:hover {
			color: var(--mountain-meadow);
		}
	}
}

.auth-form__forgot {
	display: flex;
	justify-content: flex-end;
	margin-top: -5px;
}

.auth-form__link {
	padding: 0;

	font-family: var(--font-1);
	font-size: 11px;
	font-weight: 400;
	line-height: 17px;

	color: var(--mantle);
	background: transparent;
	border: 0;

	transition: color 0.2s ease;

	@media (any-hover: hover) {
		&:hover {
			color: var(--mountain-meadow);
		}
	}
}

.auth-form__submit {
	width: 100%;
	min-height: 48px;
	justify-content: center;
	margin-top: 2px;
	padding: 13px 18px;

	font-family: var(--font-3);
	font-size: 11px;
	font-weight: 700;
	line-height: 14px;
	text-transform: uppercase;
}

.auth-message {
	display: flex;
	align-items: flex-start;
	gap: 9px;
	margin-top: 17px;
	padding: 11px 13px;

	font-family: var(--font-1);
	font-size: 12px;
	line-height: 18px;

	border-radius: 8px;

	svg {
		flex: 0 0 17px;
		width: 17px;
		height: 17px;
		margin-top: 1px;
	}

	&_success {
		color: var(--mountain-meadow);
		background: var(--mountain-meadow-10);
		border: 1px solid var(--mountain-meadow-25);
	}

	&_error {
		color: #ff8b9e;
		background: rgba(255, 71, 102, 0.08);
		border: 1px solid rgba(255, 71, 102, 0.25);
	}
}

.auth-dialog__footer {
	max-width: 340px;
	margin: 20px auto 0;

	font-family: var(--font-1);
	font-size: 10px;
	font-weight: 400;
	line-height: 16px;
	text-align: center;

	color: var(--mantle);
	opacity: 0.7;
}

.auth-content-enter-active,
.auth-content-leave-active,
.auth-message-enter-active,
.auth-message-leave-active {
	transition: opacity 0.22s ease, transform 0.22s ease;
}

.auth-content-enter,
.auth-content-leave-to {
	opacity: 0;
	transform: translateX(7px);
}

.auth-message-enter,
.auth-message-leave-to {
	opacity: 0;
	transform: translateY(-5px);
}

@media (max-width: 767.98px) {
	.auth-dialog {
		align-items: flex-start;
		padding: 18px 16px;
	}

	.auth-dialog__viewport {
		max-width: 430px;
		padding: 20px 0;
	}

	.auth-dialog__card {
		padding: 31px 24px 27px;
	}

	.auth-dialog__title {
		font-size: 24px;
		line-height: 31px;
	}
}

@media (max-width: 480px) {
	.auth-dialog {
		padding: 0;
	}

	.auth-dialog__viewport {
		min-height: 100%;
		max-width: none;
		display: flex;
		align-items: center;
		padding: 12px;
	}

	.auth-dialog__card {
		padding: 29px 18px 23px;
		border-radius: 10px;
	}

	.auth-dialog__close {
		top: 12px;
		right: 12px;
	}

	.auth-dialog__back {
		top: 15px;
		left: 13px;
	}

	.auth-dialog__header {
		padding: 0 28px;
		margin-bottom: 23px;
	}

	.auth-tabs {
		margin-bottom: 21px;
	}

	.auth-form {
		gap: 15px;
	}

	.auth-field__input {
		min-height: 44px;
	}
}
</style>
